/**
 * Metals & Non-metals Lab — deterministic property tests on elements (study-chemistry-tools-prd.md
 * §8.2 `metals-nonmetals`, §4.4 Year 8 week 2).
 *
 * Qualitative test results are reviewed teaching data in this module. Position in the periodic
 * table, state at room temperature and melting point are read from the shared periodic-table
 * dataset (`periodicTableData.ts`) so the lab and the Periodic Table tool cannot disagree.
 */

import { PERIODIC_ELEMENTS, type PeriodicElement } from './periodicTableData';

export type ElementClass = 'metal' | 'non-metal' | 'metalloid';

export type PropertyTestId = 'electrical' | 'heat' | 'lustre' | 'hammer' | 'state' | 'melting';

export type Evidence = 'metal-like' | 'non-metal-like' | 'in-between' | 'not-decisive';

export type ConductionResult = 'good' | 'semiconductor' | 'poor';
export type HeatResult = 'good' | 'moderate' | 'poor';
export type LustreResult = 'shiny' | 'dull';
export type HammerResult = 'flattens' | 'shatters' | 'not-solid';
export type RoomState = 'solid' | 'liquid' | 'gas';

export interface ElementLabSample {
  symbol: string;
  /** Australian/IUPAC spelling used in this tool. */
  name: string;
  sample: string;
  classification: ElementClass;
  electrical: ConductionResult;
  heat: HeatResult;
  lustre: LustreResult;
  hammer: HammerResult;
  /** What a learner would see for each qualitative test. */
  observations: Record<'electrical' | 'heat' | 'lustre' | 'hammer', string>;
  exception?: { title: string; note: string };
}

export interface MetalsDataProvenance {
  sources: string[];
  conditions: string;
  reviewStatus: 'pending-teaching-review';
}

export const METALS_DATA_PROVENANCE: MetalsDataProvenance = {
  sources: [
    'Airbotix periodic-table dataset (periodicTableData.ts, adapted from the MIT-licensed Zperiod dataset) — group, period, state at room temperature and melting point; values agree with the CRC Handbook of Chemistry and Physics.',
    'CRC Handbook of Chemistry and Physics — electrical resistivity and thermal conductivity of the elements (used only to rank results as good, moderate or poor).',
    'IUPAC Periodic Table of the Elements — element names and positions; boron, silicon, germanium, arsenic, antimony and tellurium treated as metalloids, as in standard Australian school texts.',
    'Standard secondary chemistry textbook descriptions of the physical properties of metals and non-metals, including the mercury, graphite, iodine and bromine exceptions.',
  ],
  conditions:
    'State at standard laboratory conditions (SLC), 25 °C and 100 kPa. Melting points at 101.325 kPa. Qualitative results describe pure samples tested with a simple battery-and-globe circuit.',
  reviewStatus: 'pending-teaching-review',
};

export const ROOM_TEMPERATURE_C = 25;

export const ELEMENT_CLASS_LABELS: Record<ElementClass, string> = {
  metal: 'Metal',
  'non-metal': 'Non-metal',
  metalloid: 'Metalloid',
};

export const PROPERTY_TESTS: readonly { id: PropertyTestId; label: string; question: string }[] = [
  { id: 'electrical', label: 'Electrical conductivity', question: 'Does the globe light when the sample completes the circuit?' },
  { id: 'heat', label: 'Heat conduction', question: 'How quickly does heat travel along the sample?' },
  { id: 'lustre', label: 'Lustre', question: 'Is a freshly cut or polished surface shiny?' },
  { id: 'hammer', label: 'Hammer test', question: 'Does the sample flatten or shatter when hammered?' },
  { id: 'state', label: 'State at 25 °C', question: 'Is it a solid, liquid or gas at room temperature?' },
  { id: 'melting', label: 'Melting point', question: 'At what temperature does it melt?' },
];

export const ELEMENT_LAB_SAMPLES: readonly ElementLabSample[] = [
  {
    symbol: 'Cu',
    name: 'Copper',
    sample: 'copper wire',
    classification: 'metal',
    electrical: 'good',
    heat: 'good',
    lustre: 'shiny',
    hammer: 'flattens',
    observations: {
      electrical: 'The globe lights brightly.',
      heat: 'Heat travels quickly along the wire.',
      lustre: 'Shiny reddish-brown surface.',
      hammer: 'Flattens without breaking.',
    },
  },
  {
    symbol: 'Fe',
    name: 'Iron',
    sample: 'iron nail',
    classification: 'metal',
    electrical: 'good',
    heat: 'good',
    lustre: 'shiny',
    hammer: 'flattens',
    observations: {
      electrical: 'The globe lights brightly.',
      heat: 'Heat travels quickly along the nail.',
      lustre: 'Shiny grey once rust is sanded off.',
      hammer: 'Bends and flattens.',
    },
  },
  {
    symbol: 'Al',
    name: 'Aluminium',
    sample: 'aluminium foil',
    classification: 'metal',
    electrical: 'good',
    heat: 'good',
    lustre: 'shiny',
    hammer: 'flattens',
    observations: {
      electrical: 'The globe lights brightly.',
      heat: 'Heat travels quickly along the strip.',
      lustre: 'Shiny silver surface.',
      hammer: 'Flattens easily — foil is rolled very thin.',
    },
  },
  {
    symbol: 'Mg',
    name: 'Magnesium',
    sample: 'magnesium ribbon',
    classification: 'metal',
    electrical: 'good',
    heat: 'good',
    lustre: 'shiny',
    hammer: 'flattens',
    observations: {
      electrical: 'The globe lights brightly.',
      heat: 'Heat travels quickly along the ribbon.',
      lustre: 'Shiny silver once the dull oxide layer is rubbed off.',
      hammer: 'Bends and flattens.',
    },
  },
  {
    symbol: 'Sn',
    name: 'Tin',
    sample: 'tin foil strip',
    classification: 'metal',
    electrical: 'good',
    heat: 'good',
    lustre: 'shiny',
    hammer: 'flattens',
    observations: {
      electrical: 'The globe lights brightly.',
      heat: 'Heat travels quickly along the strip.',
      lustre: 'Shiny silver-white surface.',
      hammer: 'Flattens easily.',
    },
  },
  {
    symbol: 'Ag',
    name: 'Silver',
    sample: 'silver wire',
    classification: 'metal',
    electrical: 'good',
    heat: 'good',
    lustre: 'shiny',
    hammer: 'flattens',
    observations: {
      electrical: 'The globe lights brightly — silver is the best electrical conductor of all elements.',
      heat: 'Heat travels very quickly along the wire.',
      lustre: 'Bright, shiny silver surface.',
      hammer: 'Flattens without breaking.',
    },
  },
  {
    symbol: 'Au',
    name: 'Gold',
    sample: 'gold leaf',
    classification: 'metal',
    electrical: 'good',
    heat: 'good',
    lustre: 'shiny',
    hammer: 'flattens',
    observations: {
      electrical: 'The globe lights brightly.',
      heat: 'Heat travels very quickly.',
      lustre: 'Shiny yellow surface that does not tarnish.',
      hammer: 'Extremely malleable — it can be beaten into leaf thinner than a cell.',
    },
  },
  {
    symbol: 'Hg',
    name: 'Mercury',
    sample: 'mercury in a sealed glass tube (data only)',
    classification: 'metal',
    electrical: 'good',
    heat: 'moderate',
    lustre: 'shiny',
    hammer: 'not-solid',
    observations: {
      electrical: 'The globe lights: liquid mercury conducts electricity.',
      heat: 'Conducts heat, but less well than most solid metals.',
      lustre: 'Shiny, mirror-like silver liquid.',
      hammer: 'Cannot be hammered — it is a liquid at room temperature.',
    },
    exception: {
      title: 'A liquid metal',
      note: 'Mercury melts at −38.83 °C, so it is liquid at room temperature. It still conducts electricity and is shiny, and it sits among the metals in Group 12. Mercury is toxic: schools use data, not samples.',
    },
  },
  {
    symbol: 'S',
    name: 'Sulfur',
    sample: 'sulfur lump',
    classification: 'non-metal',
    electrical: 'poor',
    heat: 'poor',
    lustre: 'dull',
    hammer: 'shatters',
    observations: {
      electrical: 'The globe stays off.',
      heat: 'Heat barely travels through it.',
      lustre: 'Dull yellow surface.',
      hammer: 'Shatters into powder — brittle.',
    },
  },
  {
    symbol: 'C',
    name: 'Carbon (graphite)',
    sample: 'graphite pencil core',
    classification: 'non-metal',
    electrical: 'good',
    heat: 'good',
    lustre: 'shiny',
    hammer: 'shatters',
    observations: {
      electrical: 'The globe lights: graphite conducts electricity.',
      heat: 'Heat travels well along the layers.',
      lustre: 'Grey with a slight metallic sheen.',
      hammer: 'Crumbles and flakes — brittle, not malleable.',
    },
    exception: {
      title: 'A non-metal that conducts',
      note: 'Graphite is carbon arranged in flat layers. One electron from each carbon atom is free to move along the layers, so graphite conducts electricity. It is still a non-metal: it is brittle, carbon is on the non-metal side of the staircase, and diamond (also pure carbon) does not conduct.',
    },
  },
  {
    symbol: 'I',
    name: 'Iodine',
    sample: 'iodine crystals in a sealed tube (data only)',
    classification: 'non-metal',
    electrical: 'poor',
    heat: 'poor',
    lustre: 'shiny',
    hammer: 'shatters',
    observations: {
      electrical: 'The globe stays off.',
      heat: 'Heat barely travels through it.',
      lustre: 'Shiny grey-black crystals.',
      hammer: 'Crystals crumble — brittle.',
    },
    exception: {
      title: 'A shiny non-metal',
      note: 'Iodine crystals have a metallic-looking lustre, but iodine does not conduct electricity and is brittle. Lustre alone cannot classify an element. Iodine vapour is harmful: schools use data, not open samples.',
    },
  },
  {
    symbol: 'O',
    name: 'Oxygen',
    sample: 'oxygen gas in a gas jar',
    classification: 'non-metal',
    electrical: 'poor',
    heat: 'poor',
    lustre: 'dull',
    hammer: 'not-solid',
    observations: {
      electrical: 'The globe stays off — the gas does not conduct.',
      heat: 'Gases conduct heat very poorly.',
      lustre: 'Colourless gas — no lustre.',
      hammer: 'Cannot be hammered — it is a gas.',
    },
  },
  {
    symbol: 'Br',
    name: 'Bromine',
    sample: 'bromine in a sealed ampoule (data only)',
    classification: 'non-metal',
    electrical: 'poor',
    heat: 'poor',
    lustre: 'dull',
    hammer: 'not-solid',
    observations: {
      electrical: 'The globe stays off.',
      heat: 'Conducts heat poorly.',
      lustre: 'Dark red-brown liquid giving off orange vapour — no metallic lustre.',
      hammer: 'Cannot be hammered — it is a liquid.',
    },
    exception: {
      title: 'A liquid non-metal',
      note: 'Bromine is the only non-metal that is liquid at room temperature (it melts at −7.2 °C). Being a liquid does not make an element a metal. Bromine is toxic and corrosive: data only.',
    },
  },
  {
    symbol: 'Si',
    name: 'Silicon',
    sample: 'silicon wafer piece',
    classification: 'metalloid',
    electrical: 'semiconductor',
    heat: 'good',
    lustre: 'shiny',
    hammer: 'shatters',
    observations: {
      electrical: 'The globe glows only faintly or not at all — silicon is a semiconductor.',
      heat: 'Heat travels fairly well.',
      lustre: 'Shiny blue-grey surface.',
      hammer: 'Shatters — brittle.',
    },
    exception: {
      title: 'A metalloid',
      note: 'Silicon looks metallic but is brittle, and it conducts only a little — better when warmed or when traces of other elements are added. That in-between behaviour is why it is used in computer chips and solar cells.',
    },
  },
  {
    symbol: 'Ge',
    name: 'Germanium',
    sample: 'germanium piece',
    classification: 'metalloid',
    electrical: 'semiconductor',
    heat: 'moderate',
    lustre: 'shiny',
    hammer: 'shatters',
    observations: {
      electrical: 'The globe glows only faintly or not at all — germanium is a semiconductor.',
      heat: 'Conducts heat moderately.',
      lustre: 'Shiny grey-white surface.',
      hammer: 'Shatters — brittle.',
    },
    exception: {
      title: 'A metalloid',
      note: 'Germanium is shiny like a metal but brittle like a non-metal, and it is a semiconductor. Early transistors were made from germanium.',
    },
  },
];

export interface ElementPosition {
  group: number;
  period: number;
  row: number;
  column: number;
}

export interface ElementTestRecord {
  sample: ElementLabSample;
  position: ElementPosition;
  state: RoomState;
  meltingPointC: number;
  meltingNote: string | null;
}

export interface TestReading {
  test: PropertyTestId;
  result: string;
  observation: string;
  evidence: Evidence;
}

const PHASES: Record<string, RoomState> = { Solid: 'solid', Liquid: 'liquid', Gas: 'gas' };

/** Parses a dataset melting point such as "1084.6°C", "-38.83°C" or "~3642°C (sublimes)". */
export function parseMeltingPointC(value: string): { celsius: number; note: string | null } | null {
  const match = /^~?(-?\d+(?:\.\d+)?)\s*°C(?:\s*\(([^)]+)\))?$/.exec(value.trim());
  if (!match) return null;
  return { celsius: Number(match[1]), note: match[2] ?? null };
}

/** Category from the shared dataset mapped to the three school classes; null when not mapped. */
export function classFromCategory(category: string): ElementClass | null {
  if (category === 'Metalloid') return 'metalloid';
  if (/metal$/i.test(category) && !/nonmetal$/i.test(category)) return 'metal';
  if (['Lanthanide', 'Actinide'].includes(category)) return 'metal';
  if (['Other nonmetal', 'Halogen', 'Noble Gas'].includes(category)) return 'non-metal';
  return null;
}

function periodicEntry(symbol: string): PeriodicElement | null {
  return PERIODIC_ELEMENTS.find((element) => element.symbol === symbol) ?? null;
}

export function getLabSample(symbol: string): ElementLabSample | null {
  return ELEMENT_LAB_SAMPLES.find((sample) => sample.symbol === symbol) ?? null;
}

/**
 * Builds the full test record for one element. Fails closed (returns null) when the element is
 * not a reviewed lab sample, or when its position, state or melting point is missing or unclear.
 */
export function elementTestRecord(symbol: string): ElementTestRecord | null {
  const sample = getLabSample(symbol);
  const entry = periodicEntry(symbol);
  if (!sample || !entry || entry.group === null || entry.period === null) return null;
  const state = PHASES[entry.phase];
  const melting = parseMeltingPointC(entry.meltingPoint);
  if (!state || !melting) return null;
  if ((state === 'solid') === (sample.hammer === 'not-solid')) return null;
  return {
    sample,
    position: { group: entry.group, period: entry.period, row: entry.row, column: entry.column },
    state,
    meltingPointC: melting.celsius,
    meltingNote: melting.note,
  };
}

const ELECTRICAL_EVIDENCE: Record<ConductionResult, Evidence> = {
  good: 'metal-like',
  semiconductor: 'in-between',
  poor: 'non-metal-like',
};
const HEAT_EVIDENCE: Record<HeatResult, Evidence> = { good: 'metal-like', moderate: 'in-between', poor: 'non-metal-like' };
const LUSTRE_EVIDENCE: Record<LustreResult, Evidence> = { shiny: 'metal-like', dull: 'non-metal-like' };
const HAMMER_EVIDENCE: Record<HammerResult, Evidence> = {
  flattens: 'metal-like',
  shatters: 'non-metal-like',
  'not-solid': 'not-decisive',
};

const RESULT_LABELS: Record<string, string> = {
  good: 'Good conductor',
  semiconductor: 'Semiconductor',
  poor: 'Poor conductor',
  moderate: 'Moderate conductor',
  shiny: 'Shiny',
  dull: 'Dull',
  flattens: 'Malleable',
  shatters: 'Brittle',
  'not-solid': 'Not a solid',
};

function formatCelsius(value: number): string {
  return `${value < 0 ? '−' : ''}${Math.abs(value)} °C`;
}

/** Runs one property test on a supported element. Returns null for unsupported input. */
export function runPropertyTest(symbol: string, test: PropertyTestId): TestReading | null {
  const record = elementTestRecord(symbol);
  if (!record) return null;
  const { sample } = record;
  switch (test) {
    case 'electrical':
      return { test, result: RESULT_LABELS[sample.electrical], observation: sample.observations.electrical, evidence: ELECTRICAL_EVIDENCE[sample.electrical] };
    case 'heat':
      return { test, result: RESULT_LABELS[sample.heat], observation: sample.observations.heat, evidence: HEAT_EVIDENCE[sample.heat] };
    case 'lustre':
      return { test, result: RESULT_LABELS[sample.lustre], observation: sample.observations.lustre, evidence: LUSTRE_EVIDENCE[sample.lustre] };
    case 'hammer':
      return { test, result: RESULT_LABELS[sample.hammer], observation: sample.observations.hammer, evidence: HAMMER_EVIDENCE[sample.hammer] };
    case 'state': {
      const label = record.state.charAt(0).toUpperCase() + record.state.slice(1);
      return {
        test,
        result: label,
        observation: record.state === 'gas'
          ? 'A gas at 25 °C. No metal is a gas at room temperature.'
          : `A ${record.state} at 25 °C. Both metals and non-metals can be ${record.state === 'solid' ? 'solids' : 'liquids'}, so this alone does not decide.`,
        evidence: record.state === 'gas' ? 'non-metal-like' : 'not-decisive',
      };
    }
    case 'melting': {
      const note = record.meltingNote === 'sublimes'
        ? ' At normal pressure it turns straight into a gas (sublimes) instead of melting.'
        : '';
      return {
        test,
        result: `${record.meltingNote === 'sublimes' ? 'about ' : ''}${formatCelsius(record.meltingPointC)}`,
        observation: `${record.meltingPointC > ROOM_TEMPERATURE_C ? 'Above' : 'Below'} room temperature (25 °C), so it is ${record.state === 'solid' ? 'solid' : 'not solid'} in the lab.${note} Many metals melt high, but so do carbon and silicon, and mercury melts below 0 °C — melting point alone does not classify.`,
        evidence: 'not-decisive',
      };
    }
    default:
      return null;
  }
}

export function runAllTests(symbol: string): TestReading[] | null {
  if (!elementTestRecord(symbol)) return null;
  return PROPERTY_TESTS.map((test) => runPropertyTest(symbol, test.id)).filter((reading): reading is TestReading => reading !== null);
}

export interface EvidenceTally {
  metalLike: PropertyTestId[];
  nonMetalLike: PropertyTestId[];
  inBetween: PropertyTestId[];
  notDecisive: PropertyTestId[];
  /** What the test results alone suggest, before considering structure and position. */
  suggests: ElementClass;
}

export function tallyEvidence(readings: TestReading[]): EvidenceTally {
  const pick = (evidence: Evidence) => readings.filter((reading) => reading.evidence === evidence).map((reading) => reading.test);
  const metalLike = pick('metal-like');
  const nonMetalLike = pick('non-metal-like');
  const inBetween = pick('in-between');
  const semiconductor = readings.some((reading) => reading.test === 'electrical' && reading.evidence === 'in-between');
  const suggests: ElementClass = semiconductor
    ? 'metalloid'
    : metalLike.length > nonMetalLike.length
      ? 'metal'
      : 'non-metal';
  return { metalLike, nonMetalLike, inBetween, notDecisive: pick('not-decisive'), suggests };
}

export interface ClassificationFeedback {
  correct: boolean;
  expected: ElementClass;
  headline: string;
  explanation: string;
  predictionNote: string;
  exception: ElementLabSample['exception'] | null;
  tally: EvidenceTally;
}

function testNames(ids: PropertyTestId[]): string {
  const names = ids.map((id) => PROPERTY_TESTS.find((test) => test.id === id)?.label.toLowerCase() ?? id);
  if (names.length <= 1) return names[0] ?? 'none';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function evidenceSentence(name: string, tally: EvidenceTally): string {
  const parts: string[] = [];
  if (tally.metalLike.length > 0) parts.push(`metal-like results for ${testNames(tally.metalLike)}`);
  if (tally.nonMetalLike.length > 0) parts.push(`non-metal-like results for ${testNames(tally.nonMetalLike)}`);
  if (tally.inBetween.length > 0) parts.push(`in-between results for ${testNames(tally.inBetween)}`);
  return `${name} gave ${parts.join('; ')}.`;
}

const CLASS_HINTS: Record<ElementClass, string> = {
  metal: 'Metals conduct electricity and heat, are shiny and are malleable rather than brittle.',
  'non-metal': 'Non-metals are usually poor conductors, dull and brittle when solid; many are gases.',
  metalloid: 'Metalloids are semiconductors: they conduct a little, look shiny, but are brittle.',
};

function wrongChoiceNote(sample: ElementLabSample, chosen: ElementClass): string {
  if (chosen === 'metalloid') {
    return sample.electrical === 'semiconductor'
      ? ''
      : `A metalloid is a semiconductor, but ${sample.name.toLowerCase()} is a ${sample.electrical === 'good' ? 'good' : 'poor'} conductor, not an in-between one.`;
  }
  if (chosen === 'metal' && sample.hammer === 'shatters') {
    return `A metal would flatten under the hammer, but ${sample.name.toLowerCase()} is brittle.`;
  }
  if (chosen === 'metal' && sample.electrical === 'poor') {
    return `A metal would light the globe, but ${sample.name.toLowerCase()} does not conduct electricity.`;
  }
  if (chosen === 'non-metal' && sample.hammer === 'flattens') {
    return `A solid non-metal would be brittle, but ${sample.name.toLowerCase()} flattens under the hammer.`;
  }
  if (chosen === 'non-metal' && sample.electrical === 'good') {
    return `Most non-metals do not conduct, but ${sample.name.toLowerCase()} lights the globe.`;
  }
  return '';
}

/**
 * Checks the learner's evidence-based classification and compares it with their first
 * prediction. Returns null for an unsupported element.
 */
export function checkClassification(
  symbol: string,
  answer: ElementClass,
  prediction: ElementClass | null = null,
): ClassificationFeedback | null {
  const record = elementTestRecord(symbol);
  const readings = runAllTests(symbol);
  if (!record || !readings) return null;
  const { sample } = record;
  const expected = sample.classification;
  const tally = tallyEvidence(readings);
  const correct = answer === expected;
  const evidence = evidenceSentence(sample.name, tally);
  const positionNote = `It sits in Group ${record.position.group}, Period ${record.position.period}, ${
    expected === 'metal' ? 'on the metal side of the staircase' : expected === 'metalloid' ? 'along the staircase line' : 'on the non-metal side of the staircase'
  }.`;
  const explanation = correct
    ? `${evidence} ${positionNote}`
    : [evidence, wrongChoiceNote(sample, answer), CLASS_HINTS[expected], positionNote].filter(Boolean).join(' ');
  const predictionNote = prediction === null
    ? ''
    : prediction === expected
      ? `Your first prediction (${ELEMENT_CLASS_LABELS[prediction].toLowerCase()}) was confirmed by the tests.`
      : `Your first prediction was ${ELEMENT_CLASS_LABELS[prediction].toLowerCase()}. ${
        answer === expected ? 'You changed your mind using evidence — that is how scientists classify.' : 'Compare it with the evidence above.'
      }`;
  return {
    correct,
    expected,
    headline: correct
      ? `Correct — ${sample.name} is a ${ELEMENT_CLASS_LABELS[expected].toLowerCase()}`
      : `Not quite — ${sample.name} is a ${ELEMENT_CLASS_LABELS[expected].toLowerCase()}`,
    explanation,
    predictionNote,
    exception: sample.exception ?? null,
    tally,
  };
}

export interface PeriodicMapCell {
  symbol: string;
  row: number;
  column: number;
  elementClass: ElementClass | null;
}

/** Main-block cells (Periods 1–6, Groups 1–18) built from the shared dataset. */
export const MAIN_BLOCK_MAX_ROW = 6;

export function periodicMapCells(): PeriodicMapCell[] {
  return PERIODIC_ELEMENTS.filter((element) => element.row <= MAIN_BLOCK_MAX_ROW && element.group !== null).map((element) => ({
    symbol: element.symbol,
    row: element.row,
    column: element.column,
    elementClass: classFromCategory(element.category),
  }));
}
